import React from "react";
import { useNavigate } from "react-router-dom";
import { useRenewal, RenewalPreference } from "./RenewalContext";
import RenewalSteps from "./RenewalSteps";
import ContactStep from "./steps/ContactStep";
import NotesStep from "./steps/NotesStep";
import PaymentStep from "./steps/PaymentStep";
import PmContactCard from "./steps/PmContactCard";
import MoveOutTips from "./steps/MoveOutTips";
import {
  NOT_SURE_DISCLAIMER,
  NOTICE_30_DAY,
  IMPORTANT_LEAD,
  PREFERENCE_OPTIONS,
} from "./renewalCopy";
import { Card, Notice, ReviewRow, StepHeading, StepNav } from "./renewalUi";
import { usePaymentMethods } from "../../hooks/usePaymentMethods";
import { useStepParam } from "./useStepParam";
import { useRenewalSubmit } from "./useRenewalSubmit";

const STEPS = ["Preference", "Contact", "Notes", "Payment", "Review"];

/**
 * "I'm Not Sure Yet" — a request for a renewal offer.
 *
 * Payment info is collected here too (per the updates doc) so the tenant is
 * ready to go if they do end up renewing.
 */
const NotSureFlow: React.FC = () => {
  const navigate = useNavigate();
  const { termUuid, draft, update } = useRenewal();
  const { methods } = usePaymentMethods();
  const { step, done: submitted, goTo, finish } = useStepParam(STEPS.length);
  const { submit, submitting, error: submitError } = useRenewalSubmit(finish);

  const back = () =>
    step === 1 ? navigate(`/renewal/${termUuid}`) : goTo(step - 1);
  const next = () => goTo(step + 1);

  const selectedMethod = methods.find((m) => m.id === draft.paymentMethodId);
  const methodLabel = selectedMethod
    ? `${selectedMethod.brand ?? selectedMethod.type} ••••${selectedMethod.last4 ?? ""}`
    : "—";
  const preferenceLabel =
    PREFERENCE_OPTIONS.find((o) => o.id === draft.preference)?.title ?? "—";

  if (submitted) {
    return (
      <div className="text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
          <svg
            className="h-7 w-7 text-success"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-primary">Thank you!</h2>
        {/* Constrained so the centred copy doesn't run the full width. */}
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-gray-600">
          We've notified your property manager and requested they contact you as
          soon as possible. If you have additional questions, please contact
          them directly.
        </p>

        <div className="mt-6 text-left">
          <MoveOutTips />
        </div>

        <PmContactCard />

        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="mt-6 cursor-pointer rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light"
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <div>
      <RenewalSteps steps={STEPS} current={step} badge="not-sure" />

      {step === 1 && (
        <>
          <StepHeading
            title="No problem — let's get you an offer!"
            subtitle="Tell us how long of a renewal you're looking for so your property manager can customize an offer for you."
          />

          {/* Single-select: kept as separate tappable cards (a divided list
              would blur where one option ends and the next begins), but
              tighter than before and with an explicit radio indicator so the
              selected state doesn't rely on the border colour alone. */}
          <div className="space-y-2">
            {PREFERENCE_OPTIONS.map((opt) => {
              const selected = draft.preference === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() =>
                    update({ preference: opt.id as RenewalPreference })
                  }
                  className={`flex w-full cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                    selected
                      ? "border-primary bg-primary/5"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                      selected ? "border-primary" : "border-gray-300"
                    }`}
                  >
                    {selected && (
                      <span className="h-2 w-2 rounded-full bg-primary" />
                    )}
                  </span>
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-primary">
                        {opt.title}
                      </span>
                      {opt.tag && (
                        <span className="rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-semibold text-success">
                          {opt.tag}
                        </span>
                      )}
                    </span>
                    {opt.subtitle && (
                      <span className="mt-0.5 block text-xs leading-relaxed text-gray-600">
                        {opt.subtitle}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-4">
            <Notice tone="warning">
              <strong>{IMPORTANT_LEAD}</strong> {NOT_SURE_DISCLAIMER.before}
              <strong className="font-semibold">
                {NOT_SURE_DISCLAIMER.emphasis}
              </strong>
              {NOT_SURE_DISCLAIMER.after}
            </Notice>
          </div>

          <StepNav
            onBack={back}
            onNext={next}
            nextDisabled={!draft.preference}
          />
        </>
      )}

      {step === 2 && <ContactStep onBack={back} onNext={next} />}
      {step === 3 && <NotesStep onBack={back} onNext={next} />}
      {step === 4 && <PaymentStep onBack={back} onNext={next} />}

      {step === 5 && (
        <>
          <StepHeading
            title="Review & Submit"
            subtitle="Double-check everything before it goes to your PM."
          />

          <Card className="p-0">
            <ReviewRow label="Intent" value="Requesting a Renewal Offer" />
            <ReviewRow label="Requested Term" value={preferenceLabel} />
            <ReviewRow label="Name" value={draft.contact.name} />
            <ReviewRow label="Email" value={draft.contact.email} />
            <ReviewRow label="Phone" value={draft.contact.phone} />
            <ReviewRow label="Payment Method" value={methodLabel} />
            <ReviewRow
              label="Notes"
              value={
                draft.notes.hasNotes && draft.notes.text
                  ? draft.notes.text
                  : "None"
              }
            />
            <div className="px-5 py-3">
              <button
                type="button"
                onClick={() => goTo(1)}
                className="cursor-pointer text-sm font-semibold text-primary underline"
              >
                ← Make Changes
              </button>
            </div>
          </Card>

          <div className="mt-4">
            <Notice tone="warning">
              <strong>{IMPORTANT_LEAD}</strong> {NOTICE_30_DAY}
            </Notice>
          </div>

          {submitError && (
            <p className="mb-3 text-sm font-medium text-danger">{submitError}</p>
          )}

          <StepNav
            onBack={back}
            onNext={() => submit("not-sure")}
            nextLabel={
              submitting ? "Submitting…" : "Submit to Property Manager ✓"
            }
            nextDisabled={submitting}
            nextVariant="success"
          />
        </>
      )}
    </div>
  );
};

export default NotSureFlow;
